import React, { useCallback, useEffect, useState } from 'react';
import { Box, Tab, Tabs } from '@mui/material';

import {
  changeUserPassword,
  deleteQuilt,
  editableQuiltsList,
  getProfile,
  getUserQuilts,
  inviteUserToQuilt,
  removeUserAccess,
  updateProfile,
} from 'api/http-client/queries/profileQueries';
import { publicPrivate, publishDraft } from 'api/http-client/queries/quilts';
import { TProfile, TRemoveAccess, TShareQuilt, TUserQuilt } from 'api/http-client/profileTypes';
import { colors, notifyMessages } from 'utilities';
import { useTranslates } from 'providers/translates';
import { useAppDispatch } from 'hooks';
import { setOpen } from 'store/reducers/notifySlice';
import { Language } from 'providers/translates/enums';
import { isVac } from 'utilities/is-vac';
import { Helmet } from 'react-helmet-async';

import { AccessPermissionsTextEnum } from 'components/CreateMemorialQuiltComponents/CreateMemorialQuiltStepTwo/const';
import { Loader } from 'components/Loader/Loader';
import AdminDashboard from 'pages/AdminDashboard';

import { Welcome } from './Welcome';
import { MyQuilts } from './MyQuilts';
import { UserSettings } from './UserSettings';

import styles from './styles.module.scss';
import { 
  STEP_ONE_SNAPSHOT_KEY, 
  STEP_THREE_SNAPSHOT_KEY, 
  STEP_TWO_SNAPSHOT_KEY 
} from 'components/CreateMemorialQuiltComponents/constants/constants';
import { SyntheticEvent } from 'react-toastify/dist/utils';

interface TabPanelProps {
  children?: React.ReactNode;
  index: string;
  value: string;
}

export enum ProfileTab {
  Welcome = 'Welcome',
  MyQuilts = 'MyQuilts',
  UserSettings = 'UserSettings',
  AdminPanel = 'AdminPanel',
}

const translationsHelmet = {
  titleEN: 'Saluting Service - Your profile',
  titleFR: 'Saluons leur service - Votre profil'
} as const;

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ px: 0, py: 3 }}>{children}</Box>}
    </div>
  );
}

const handleChange = (event: React.SyntheticEvent, newValue: ProfileTab) => {
  window.location.hash = newValue;
};

export const UserProfile: React.FC = () => {
  const { translates: t, selectedLang } = useTranslates();

  const isFrench = selectedLang === Language.FRENCH;

  const welcomeUser = t?.welcomeUser;
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<TProfile | null>(null);
  const [quiltsList, setQuiltsList] = useState<TUserQuilt[] | null>(null);
  const [editableList, setEditableList] = useState<TUserQuilt[] | null>(null);
  const [errorPrivateDialog, setErrorPrivateDialog] = useState(false);

  const [tab, setTab] = useState<ProfileTab>(ProfileTab.Welcome);

  const requestUserData = useCallback(() => {
    return Promise.all([
      getProfile().then((resp) => setUser(resp)),
      getUserQuilts().then((resp) => setQuiltsList(resp)),
      editableQuiltsList().then((resp) => {
        // without no edit
        const editableListData = resp.filter(
          (item) => item.access !== AccessPermissionsTextEnum.NO_EDIT
        );

        setEditableList(editableListData);
      }),
    ]);
  }, []);

  useEffect(() => {
    setLoading(true);

    requestUserData().then(() => {
      setLoading(false);
    });
  }, [requestUserData]);

  const publicUnpublic = (quilt: TUserQuilt) => {
    if (!quilt.finalImage && !quilt.isPublic && quilt.isPublished) {
      dispatch(setOpen(notifyMessages.quiltPublicImageError));
      return;
    }

    setLoading(true);
    publicPrivate(quilt.id, quilt.isPublic).finally(() => {
      if (quilt.isPublic) {
        setErrorPrivateDialog(true);
      } else {
        dispatch(setOpen(t?.notifyMessages?.quiltChanged || notifyMessages.quiltChanged));
      }
      requestUserData();
      setLoading(false);
    });
  };

  const publishUnpublish = (quilt: TUserQuilt) => {
    if (!quilt.finalImage && quilt.isPublic) {
      dispatch(setOpen(notifyMessages.quiltPublicImageError));
      return;
    }

    setLoading(true);
    publishDraft(quilt.id, quilt.isPublished).finally(() => {
      dispatch(setOpen(t?.notifyMessages?.quiltChanged || notifyMessages.quiltChanged));
      requestUserData();
      setLoading(false);
    });
  };

  const shareQuilt = (body: TShareQuilt) => {
    inviteUserToQuilt(body).finally(() => {
      requestUserData();
      dispatch(setOpen(t?.notifyMessages?.accessAllowed || notifyMessages.accessAllowed));
    });
  };
  const removeAccess = (body: TRemoveAccess) => {
    removeUserAccess(body).finally(() => {
      requestUserData();
      dispatch(setOpen(t?.notifyMessages?.accessBlocked || notifyMessages.accessBlocked));
    });
  };
  const deleteUserQuilt = (id: string) => {
    setLoading(true);
    deleteQuilt(id).finally(() => {
      requestUserData();
      setLoading(false);
      dispatch(setOpen(t?.notifyMessages?.quiltDeleted || notifyMessages.quiltDeleted));
    });
  };

  const saveUserData = (data: TProfile) => {
    setLoading(true);

    updateProfile(data)
      .then(() => getProfile())
      .then((resp) => {
        setUser(resp);
        dispatch(
          setOpen(t?.notifyMessages?.savedSettings || notifyMessages.savedSettings)
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const changePassword = (password: string) => {
    changeUserPassword(password).finally(() =>
      dispatch(
        setOpen(t?.notifyMessages?.passwordChanged || notifyMessages.passwordChanged)
      )
    );
  };

  useEffect(() => {
    localStorage.removeItem(STEP_ONE_SNAPSHOT_KEY);
    localStorage.removeItem(STEP_TWO_SNAPSHOT_KEY);
    const hashChangeEvent = () => {
      let hash = window.location.hash.replace('#', '');
      if (!hash) {
        hash = ProfileTab.Welcome;
      }
      setTab(hash as ProfileTab);
    };
    hashChangeEvent();
    window.addEventListener("hashchange", hashChangeEvent);
    return () => {
      window.removeEventListener("hashchange", hashChangeEvent);
    };
  }, []);

  return (
    <>
    {isVac() && (
      <Helmet>
        <title>{isFrench ? translationsHelmet?.titleFR : translationsHelmet?.titleEN}</title>
      </Helmet>
    )}
    <div className={styles.wrap}>
      {user ? (
        <Box className="center">
          <Tabs
            className='ProfileTabs'
            value={tab}
            onChange={handleChange}
            variant="scrollable"
            allowScrollButtonsMobile
            textColor="primary"
          >
            <Tab
              className='ProfileTab'
              value={ProfileTab.Welcome}
              label={welcomeUser?.welcomeUserTabOne ?? "Welcome"}
              sx={{ color: colors.blue, fontSize: { xs: 14, sm: 18 } }}
            />
            <Tab
              className='ProfileTab'
              value={ProfileTab.MyQuilts}
              label={welcomeUser?.welcomeUserTabTwo ?? "My Quilts"}
              sx={{ color: colors.blue, fontSize: { xs: 14, sm: 18 } }}
            />
            <Tab
              className='ProfileTab'
              value={ProfileTab.UserSettings}
              label={welcomeUser?.welcomeUserTabThree ?? "User Settings"}
              sx={{ color: colors.blue, fontSize: { xs: 14, sm: 18 } }}
            />
            {user.isAdmin && (
              <Tab
                className='ProfileTab'
                value={ProfileTab.AdminPanel}
                label={welcomeUser?.welcomeUserTabFour ?? "Admin Panel"}
                sx={{ color: colors.blue, fontSize: { xs: 14, sm: 18 } }}
              />
            )}
          </Tabs>
          <CustomTabPanel value={tab} index={ProfileTab.Welcome}>
            <Welcome user={user} />
          </CustomTabPanel>
          <CustomTabPanel value={tab} index={ProfileTab.MyQuilts}>
            <MyQuilts
              quiltsList={quiltsList}
              user={user}
              onSetErrorPrivatDialog={setErrorPrivateDialog}
              publicUnpublic={publicUnpublic}
              editableList={editableList}
              publishUnpublish={publishUnpublish}
              shareQuilt={shareQuilt}
              removeAccess={removeAccess}
              deleteUserQuilt={deleteUserQuilt}
              errorPrivateDialog={errorPrivateDialog}
            />
          </CustomTabPanel>
          <CustomTabPanel value={tab} index={ProfileTab.UserSettings}>
            <UserSettings user={user} changePassword={changePassword} saveUserData={saveUserData} />
          </CustomTabPanel>
          {user.isAdmin && (
            <CustomTabPanel value={tab} index={ProfileTab.AdminPanel}>
              <AdminDashboard />
            </CustomTabPanel>
          )}
        </Box>
      ) : (
        <span className={styles.noUser}>{welcomeUser?.noUser ?? 'No user!'}</span>
      )}
      <Loader loading={loading} />
    </div>
    </>
  );
};
